<script lang="ts">
	// UC-63: read one shift afresh on every open, including late payments.
	// The server owns every figure; this sheet only formats its answer.
	import { untrack } from 'svelte';
	import Band from '../components/Band.svelte';
	import Chip from '../components/Chip.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import Sheet from '../components/Sheet.svelte';
	import StatCard from '../components/StatCard.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { api } from '../api/client';
	import type { components } from '../api/types';
	import { fill, isArabic, message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import { orderNumber } from '../format/order';
	import {
		anyExtra,
		driverShortExtra,
		dueRowLabel,
		overShort,
		readsExtra,
		showsDrivers,
	} from '../format/shift';
	import { formatClock, formatDate } from '../format/time';

	interface Props {
		language: Language;
		minorDigits: number;
		shiftId: string;
		tillName: string;
		onClose: () => void;
	}
	let { language, minorDigits, shiftId, tillName, onClose }: Props = $props();
	let report = $state<components['schemas']['ShiftReportBody'] | null>(null);
	let loading = $state(false);
	let failure = $state('');
	// Try again only after the network or the server failed (web.md, Errors).
	let retryable = $state(false);
	let asked = 0;

	async function load(id: string) {
		const ticket = ++asked;
		report = null;
		failure = '';
		retryable = false;
		loading = true;
		try {
			const { data, error, response } = await api.GET('/api/owner/shifts/{shiftId}', {
				params: { path: { shiftId: id } },
			});
			if (ticket !== asked) return;
			if (error !== undefined || !data) {
				failure = error?.message ?? message(language, 'loadFailed');
				retryable = response.status >= 500;
				return;
			}
			report = data;
		} catch {
			if (ticket !== asked) return;
			failure = message(language, 'loadFailed');
			retryable = true;
		} finally {
			if (ticket === asked) loading = false;
		}
	}

	$effect(() => {
		const id = shiftId;
		if (id) untrack(() => void load(id));
		return () => {
			++asked;
		};
	});

	function when(iso: string): string {
		return `${formatDate(iso, language)} ${formatClock(iso, language)}`;
	}
	// The cash movements' columns: time, out or in, amount, note or driver, by whom.
	const movementColumns = $derived([
		{ label: message(language, 'ordersTime'), w: '84px' },
		{ label: message(language, 'ordersType'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'sellTotal'), w: '104px', align: 'end' as const },
		{ label: '', w: 'minmax(0,2fr)' },
		{ label: '', w: 'minmax(0,1fr)' },
	]);
	// The close's split of Due at close (UC-63.R7): driver, orders, amount.
	const dueColumns = $derived([
		{ label: message(language, 'shiftsDueByDriver'), w: 'minmax(0,2fr)' },
		{ label: message(language, 'reportsOrders'), w: '84px', align: 'end' as const },
		{ label: message(language, 'sellTotal'), w: '104px', align: 'end' as const },
	]);
	// The close's rows per driver (UC-63.R8), in the API's columns.
	const driverColumns = $derived([
		{ label: message(language, 'driversTitle'), w: 'minmax(56px,1fr)' },
		{
			label: message(language, 'shiftsDriverCollected'),
			w: 'minmax(64px,1fr)',
			align: 'end' as const,
		},
		{
			label: message(language, 'shiftsDriverHanded'),
			w: 'minmax(64px,1fr)',
			align: 'end' as const,
		},
		{
			label: message(language, 'shiftsDriverShortExtra'),
			w: 'minmax(104px,1.4fr)',
			align: 'end' as const,
		},
		{
			label: message(language, 'shiftsDriverFloats'),
			w: 'minmax(56px,1fr)',
			align: 'end' as const,
		},
		{
			label: message(language, 'shiftsDriverRefused'),
			w: 'minmax(40px,0.6fr)',
			align: 'end' as const,
		},
		{
			label: message(language, 'shiftsDriverPaidDirect'),
			w: 'minmax(40px,0.6fr)',
			align: 'end' as const,
		},
		{
			label: message(language, 'shiftsDriverBalance'),
			w: 'minmax(64px,1fr)',
			align: 'end' as const,
		},
	]);
	const extraShown = $derived(!!report && anyExtra(report));
	const columns = $derived([
		{ label: message(language, 'ordersNumber'), w: '64px' },
		{ label: message(language, 'shiftsTill'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'ordersTime'), w: '84px' },
		{ label: message(language, 'ordersType'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'sellTotal'), w: '104px', align: 'end' as const },
		{ label: '', w: 'minmax(0,1fr)' },
	]);
</script>

<Sheet
	open={!!shiftId}
	title={fill(message(language, 'shiftsReportTitle'), report?.tillName || tillName)}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="shift-report-close"
	{onClose}
	testId="shift-report"
>
	<div class="sheet" data-scroll="">
		{#if loading}
			<div class="wait">
				<Loading
					size="lg"
					label={message(language, 'loading')}
					caption={message(language, 'loading')}
				/>
			</div>
		{:else if failure}
			<Band
				tone="flag"
				testId="shift-report-error"
				action={retryable ? message(language, 'retry') : undefined}
				actionTestId="shift-report-retry"
				onAction={() => void load(shiftId)}>{failure}</Band
			>
		{:else if report}
			<div class="stats">
				<div class="stat">
					<StatCard
						label={message(language, 'shiftsOverShort')}
						value={report.closedAt && report.overShortMinor !== undefined
							? overShort(language, report.overShortMinor, minorDigits)
							: '–'}
						tone="ledger"
						mono
						testId="shift-report-overshort"
					/>
				</div>
				<div class="stat">
					<StatCard
						label={message(language, 'shiftExpected')}
						value={formatMinor(report.expectedMinor, minorDigits)}
						mono
						testId="shift-report-expected"
					/>
				</div>
				<div class="stat">
					<StatCard
						label={message(language, 'shiftCounted')}
						value={report.closedAt && report.countedMinor !== undefined
							? formatMinor(report.countedMinor, minorDigits)
							: '–'}
						mono
						testId="shift-report-counted"
					/>
				</div>
			</div>
			{#if !report.closedAt}
				<div class="open">
					<Chip
						text={message(language, 'shiftsOpenChip')}
						tone="flag"
						dot
						testId="shift-report-open"
					/>
				</div>
			{/if}
			<Band testId="shift-report-figures">
				<span class="figures">
					<span data-testid="shift-report-opened"
						>{fill(
							message(language, 'shiftsOpenedLine'),
							when(report.openedAt),
							report.openedBy,
						)}</span
					>
					{#if report.closedAt}<span data-testid="shift-report-closed"
							>{fill(
								message(language, 'shiftClosedBy'),
								when(report.closedAt),
								report.closedBy ?? '',
							)}</span
						>{/if}
					{#if report.approvedBy}<span data-testid="shift-report-approved"
							>{fill(message(language, 'shiftApprovedBy'), report.approvedBy)}</span
						>{/if}
					<span data-testid="shift-report-float"
						>{message(language, 'shiftFloat')}
						<bdi dir="ltr">{formatMinor(report.floatMinor, minorDigits)}</bdi></span
					>
					<span data-testid="shift-report-cash"
						>{message(language, 'shiftCashSales')}
						<bdi dir="ltr">{formatMinor(report.cashMinor, minorDigits)}</bdi></span
					>
					<span data-testid="shift-report-card"
						>{message(language, 'shiftCardSales')}
						<bdi dir="ltr">{formatMinor(report.cardMinor, minorDigits)}</bdi></span
					>
					<span data-testid="shift-report-voids"
						>{message(language, 'shiftVoids')}
						<bdi dir="ltr"
							>({report.voidCount}) {formatMinor(
								report.voidCashMinor + report.voidCardMinor,
								minorDigits,
							)}</bdi
						></span
					>
					{#if report.movements?.length}<span data-testid="shift-report-cash-out"
							>{message(language, 'shiftsCashOut')}
							<bdi dir="ltr">{formatMinor(report.cashOutMinor, minorDigits)}</bdi></span
						><span data-testid="shift-report-cash-in"
							>{message(language, 'shiftsCashIn')}
							<bdi dir="ltr">{formatMinor(report.cashInMinor, minorDigits)}</bdi></span
						>{/if}
					<span data-testid="shift-report-nosale"
						>{fill(message(language, 'shiftsNoSaleOpensOn'), report.tillName)}
						<bdi dir="ltr">{report.noSaleOpens}</bdi></span
					>
					{#if report.closedAt}<span data-testid="shift-report-due"
							>{fill(message(language, 'shiftsDueOn'), report.tillName || tillName)}
							<bdi dir="ltr">({report.dueCount}) {formatMinor(report.dueMinor, minorDigits)}</bdi
							></span
						>{/if}
				</span>
			</Band>
			{#if report.closedAt && report.dueByDriver?.length}
				<TablePanel
					columns={dueColumns}
					title={fill(message(language, 'shiftsDueOn'), report.tillName || tillName)}
					testId="shift-report-due-by-driver"
				>
					{#each report.dueByDriver as row, i (i)}
						<Row testId="shift-due-{i}">
							<span>{dueRowLabel(language, row)}</span>
							<bdi class="amount" dir="ltr">{row.count}</bdi>
							<bdi class="amount" dir="ltr">{formatMinor(row.amountMinor, minorDigits)}</bdi>
						</Row>
					{/each}
				</TablePanel>
				<p class="note" data-testid="shift-report-due-note">
					{message(language, 'shiftsDueByDriverNote')}
				</p>
			{/if}
			{#if showsDrivers(report)}
				<TablePanel
					columns={driverColumns}
					title={message(language, 'driversTitle')}
					testId="shift-report-drivers"
				>
					{#each report.drivers ?? [] as driver, i (i)}
						<Row testId="shift-driver-{i}">
							<span>{driver.name}</span>
							<bdi class="amount" dir="ltr">{formatMinor(driver.collectedMinor, minorDigits)}</bdi>
							<bdi class="amount" dir="ltr">{formatMinor(driver.handedMinor, minorDigits)}</bdi>
							<span class="amount figure" class:extra={readsExtra(driver)}
								>{driverShortExtra(language, driver.shortExtraMinor, minorDigits)}</span
							>
							<bdi class="amount" dir="ltr">{formatMinor(driver.floatsMinor, minorDigits)}</bdi>
							<bdi class="amount" dir="ltr">{driver.refused}</bdi>
							<bdi class="amount" dir="ltr">{driver.paidDirect}</bdi>
							<bdi class="amount" dir="ltr">{formatMinor(driver.balanceMinor, minorDigits)}</bdi>
						</Row>
					{/each}
				</TablePanel>
				{#if extraShown}
					<p class="note" data-testid="shift-report-drivers-extra">
						{message(language, 'shiftsDriverExtraNote')}
					</p>
				{/if}
			{/if}
			{#if report.unreached?.length}
				<Band tone="flag" testId="shift-report-unreached"
					>{fill(
						message(language, 'shiftNotReached'),
						report.unreached
							.map((till) => till.tillName || till.tillId)
							.join(isArabic(language) ? '، ' : ', '),
					)}</Band
				>
			{/if}
			{#if report.figuresClamped}
				<Band tone="flag" testId="shift-report-clamped"
					>{message(language, 'shiftsFiguresClamped')}</Band
				>
			{/if}
			{#if report.movements?.length}
				<TablePanel
					columns={movementColumns}
					title={message(language, 'shiftsMovementsHeading')}
					testId="shift-report-movements"
				>
					{#each report.movements as movement, i (i)}
						<Row testId="shift-movement-{i}">
							<bdi dir="ltr">{formatClock(movement.at, language)}</bdi>
							<span
								>{message(
									language,
									movement.direction === 'out' ? 'shiftsCashOut' : 'shiftsCashIn',
								)}</span
							>
							<bdi class="amount" dir="ltr">{formatMinor(movement.amountMinor, minorDigits)}</bdi>
							<span
								>{movement.driverName
									? fill(message(language, 'shiftsMovementDriver'), movement.driverName)
									: movement.note || movement.reason}</span
							>
							<span
								>{movement.byName
									? fill(message(language, 'shiftsMovementBy'), movement.byName)
									: ''}</span
							>
						</Row>
					{/each}
				</TablePanel>
			{/if}
			{#if !report.sales?.length}
				<p class="none" data-testid="shift-report-no-sales">{message(language, 'shiftsNoSales')}</p>
			{:else}
				<TablePanel
					{columns}
					title={message(language, 'shiftsSalesHeading')}
					testId="shift-report-sales"
				>
					{#each report.sales as sale (`${sale.orderId}-${sale.kind}`)}
						<Row testId="shift-sale-{sale.orderId}-{sale.kind}">
							<bdi dir="ltr">{orderNumber(sale)}</bdi>
							<span>{sale.tillName}</span>
							<bdi dir="ltr">{formatClock(sale.at, language)}</bdi>
							<span
								>{message(language, sale.kind === 'sale' ? 'shiftsSale' : 'shiftsVoid')} · {message(
									language,
									sale.method === 'cash' ? 'ordersCash' : 'ordersCard',
								)}</span
							>
							<bdi class="amount" dir="ltr"
								>{formatMinor(
									sale.kind === 'void' ? -sale.amountMinor : sale.amountMinor,
									minorDigits,
								)}</bdi
							>
							<span
								>{#if sale.arrivedAfterClose}<Chip
										text={message(language, 'shiftsArrivedAfterClose')}
										tone="flag"
										size="sm"
										testId="shift-sale-late-{sale.orderId}-{sale.kind}"
									/>{/if}</span
							>
						</Row>
					{/each}
				</TablePanel>
			{/if}
		{/if}
	</div>
</Sheet>

<style>
	.sheet {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}
	/* The sheet scrolls; its parts keep their own height instead of shrinking to fit, where a table would clip its rows. */
	.sheet > :global(*) {
		flex: none;
	}
	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}
	.stats {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-5);
	}
	.stat {
		flex: 1 1 200px;
		min-inline-size: 0;
	}
	.open {
		display: flex;
	}
	.figures {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3) var(--space-7);
	}
	.amount {
		text-align: end;
		font-family: var(--font-mono);
	}
	.figure {
		white-space: nowrap;
	}
	/* An extra shows in the over colour: unexplained cash, never revenue or a tip. */
	.extra {
		color: var(--accent-visibility);
		font-weight: 700;
	}
	.note {
		margin: 0;
		font-size: var(--text-sm);
		color: var(--text-muted);
	}
	.none {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}
</style>
